import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  Calendar, MapPin, ArrowRight, Star,
  Code, TrendingUp, Building2, ChevronDown, ChevronUp, ArrowLeft, Globe, ExternalLink
} from 'lucide-react';
import { Section, SectionHeader } from '../components/ui/Section';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';

export default function Experience() {
  const [activeExperience, setActiveExperience] = useState(0); // Flagship role default open
  const [isManuallySelected, setIsManuallySelected] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const experiences = [
    {
      id: 1,
      role: "Senior Software Developer",
      company: "Foxwel.ai",
      website: "https://www.foxwel.ai/",
      location: "Remote (Hybrid)",
      duration: "Mar 2026 - Present",
      type: "Full-Time",
      description: "Designs the architecture of an all-in-one business operations platform for client companies and leads the team building it, with the Gemini API built into the product.",
      achievements: [
        "Designing the architecture for an all-in-one business platform for client companies, covering team chat, orders, the product catalog and inventory, and Google Drive-style file storage",
        "Lead the team through the build, from the data model and API contracts to each release",
        "Built the file storage module (folders, uploads, sharing, role-based permissions) in Next.js, TypeScript and Node.js, and integrated the Gemini API into the platform",
      ],
      technologies: ["React", "Next.js", "TypeScript", "Node.js", "Gemini API", "REST APIs", "RBAC", "Tailwind CSS"],
      impact: { role: "Lead architect", stack: "Next.js · TS", aiModel: "Gemini" }
    },
    {
      id: 2,
      role: "Software Development Engineer (Product Owner)",
      company: "Anave Enterprises LLP",
      website: "https://www.anaventerprise.com/",
      location: "Bengaluru, India",
      duration: "May 2025 - Jul 2026",
      type: "Full-Time",
      description: "Led architecture and design for the products of a scale-model hobby company, working with the team on HobbyistDecals.com, a decal marketplace shipping to 42 countries, and contributing to Skyskale.com.",
      products: [
        { name: "HobbyistDecals.com", url: "https://hobbyistdecals.com", note: "Decal marketplace · 3,400+ SKUs · 42 countries" },
        { name: "Skyskale.com", url: "https://skyskale.com", note: "Modelling-tools storefront" },
      ],
      achievements: [
        "Led architecture and design for HobbyistDecals.com, a MERN decal marketplace. Worked with the team on custom order intake, a design approval loop between customers and the studio, and customer messaging",
        "Wrote the REST APIs for order tracking, split payments, affiliate marketing and loyalty points, and integrated the payment gateway and Shiprocket",
        "Added a RAG chatbot over the catalog and order history, built with LangChain and traced in LangSmith, so customers can ask about products and their orders",
        "Shipped 3 React and Next.js web apps used by 500+ active users, with server-side rendering and per-user access control",
        "Rebuilt Google Drive-style file management inside the company CRM on the Drive API, including nested folders, sharing and permissions",
        "Built React Native apps, one of them with real-time chat and video calling",
        "Brought page load down from 5s to 0.5s by refactoring, and cut deploy time by 60% with GitHub Actions",
      ],
      technologies: ["React", "Next.js", "Node.js", "Express", "MongoDB", "React Native", "LangChain", "LangSmith", "RAG", "REST APIs", "AWS S3", "GitHub Actions"],
      impact: { pageLoad: "5s → 0.5s", activeUsers: "500+", deployTimeSaved: "60%" }
    },
    {
      id: 3,
      role: "Full Stack and AI/ML Intern",
      company: "Accolade Tech Solutions",
      location: "Mangalore, India",
      duration: "Jan 2025 - May 2025",
      type: "Internship",
      description: "Conducted research on deep learning models for computer vision applications. Developed full-stack solutions and mobile applications, contributing to client projects.",
      achievements: [
        "Developed machine learning models achieving 95% prediction accuracy on benchmark datasets",
        "Built full-stack web applications tailored to client-specific requirements and use cases",
        "Created a voice-powered voting system for visually impaired users, enhancing accessibility",
      ],
      technologies: ["Python", "TensorFlow", "PyTorch", "OpenCV", "Flask", "MySQL", "Flutter"],
      impact: { modelAccuracy: "95%", projectsCompleted: "3", projectsContributed: "5" }
    },
    {
      id: 4,
      role: "AI/ML Intern",
      company: "Entertainment Technologists",
      location: "Remote",
      duration: "Sep 2024 - Nov 2024",
      type: "Internship",
      description: "Contributed to machine learning projects by leveraging open-source models. Enhanced web interfaces and optimized model performance during a three-month remote internship.",
      achievements: [
        "Redesigned web interfaces applying modern UI/UX principles for improved usability",
        "Improved model performance by 70% through integration of advanced open-source models",
        "Added multilingual result download functionality based on user language preferences",
      ],
      technologies: ["Python", "Flask", "HTML", "CSS", "JavaScript", "Open Source Models"],
      impact: { projectsCompleted: "1", performanceImprovement: "70%", userSatisfaction: "98%" }
    },
    {
      id: 5,
      role: "Full Stack Developer & Lead",
      company: "College Alumni Club",
      location: "Karnataka, India",
      duration: "Nov 2024 - Feb 2025",
      type: "Leadership",
      description: "Led the development of an alumni portal to connect students and alumni, focusing on robust backend architecture and performance optimization.",
      achievements: [
        "Designed and developed the backend system using Django and MySQL for scalability",
        "Implemented database triggers to enhance data management efficiency and integrity",
        "Reduced website latency by 50% through caching strategies and query optimization",
      ],
      technologies: ["HTML", "CSS", "JavaScript", "Django", "MySQL", "Git", "GitHub"],
      impact: { usersSupported: "200+", eventsOrganized: "4", communityGrowth: "+150%" }
    }
  ];

  useEffect(() => {
    if (isManuallySelected || isMobile || isHovered || reduceMotion) return;

    const interval = setInterval(() => {
      setActiveExperience(prev => (prev + 1) % experiences.length);
    }, 4500);

    return () => clearInterval(interval);
  }, [isManuallySelected, isMobile, isHovered, reduceMotion, experiences.length]);

  const handleExperienceClick = (index) => {
    if (isMobile) {
      setActiveExperience(activeExperience === index ? -1 : index);
    } else {
      setActiveExperience(index);
    }
    setIsManuallySelected(true);

    if (!isMobile) {
      setTimeout(() => {
        setIsManuallySelected(false);
      }, 12000);
    }
  };

  const currentExp = experiences[activeExperience >= 0 ? activeExperience : 0];

  return (
    <Section id="experience">
      <SectionHeader
        title="Experience & Growth"
        subtitle="Verified track record across full-stack engineering, AI/ML pipelines, and technical leadership."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Timeline Navigation List */}
        <div className="space-y-3">
          {experiences.map((exp, index) => {
            const isActive = activeExperience === index;
            return (
              <Card
                key={exp.id}
                interactive
                onClick={() => handleExperienceClick(index)}
                className={`p-4 transition-colors ${
                  isActive ? 'bg-[var(--bg-hover)]' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="t-label text-[var(--fg-primary)] font-medium leading-tight">
                    {exp.role}
                  </h3>
                  <Badge>{exp.type}</Badge>
                </div>

                <p className="text-xs text-[var(--fg-secondary)] mb-2 font-medium">{exp.company}</p>
                <div className="flex items-center gap-1.5 text-xs text-[var(--fg-secondary)]">
                  <Calendar size={12} />
                  <span>{exp.duration}</span>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Right Column: Detailed View */}
        <div 
          className="lg:col-span-2"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {activeExperience >= 0 && currentExp ? (
            <Card className="p-6 md:p-8 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-[var(--bg-recessed)]">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 size={20} className="text-[var(--fg-secondary)]" />
                    <h3 className="t-h4 text-[var(--fg-primary)]">{currentExp.role}</h3>
                  </div>
                  {currentExp.website ? (
                    <a
                      href={currentExp.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-medium text-[var(--fg-primary)] hover:underline underline-offset-4"
                    >
                      {currentExp.company}
                      <ExternalLink size={12} className="text-[var(--fg-muted)]" />
                    </a>
                  ) : (
                    <p className="text-sm font-medium text-[var(--fg-primary)]">{currentExp.company}</p>
                  )}
                </div>

                <div className="flex flex-col sm:items-end gap-1 text-xs text-[var(--fg-secondary)]">
                  <span className="flex items-center gap-1">
                    <Calendar size={12} />
                    {currentExp.duration}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin size={12} />
                    {currentExp.location}
                  </span>
                </div>
              </div>

              <p className="text-sm text-[var(--fg-secondary)] leading-relaxed">
                {currentExp.description}
              </p>

              {currentExp.products?.length > 0 && (
                <div>
                  <h4 className="t-eyebrow text-[var(--fg-primary)] mb-3 flex items-center gap-1.5">
                    <Globe size={14} className="text-[var(--fg-secondary)]" />
                    Products Shipped
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {currentExp.products.map((product) => (
                      <a
                        key={product.name}
                        href={product.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center justify-between gap-3 p-3 rounded-md bg-[var(--bg-recessed)] hover:bg-[var(--bg-hover)] transition-colors"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[var(--fg-primary)] truncate">{product.name}</p>
                          <p className="text-xs text-[var(--fg-secondary)] leading-snug">{product.note}</p>
                        </div>
                        <ExternalLink size={14} className="shrink-0 text-[var(--fg-muted)] group-hover:text-[var(--fg-primary)] transition-colors" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h4 className="t-eyebrow text-[var(--fg-primary)] mb-3 flex items-center gap-1.5">
                  <Star size={14} className="text-[var(--fg-secondary)]" />
                  Key Achievements
                </h4>
                <ul className="space-y-2">
                  {currentExp.achievements.map((ach, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-[var(--fg-secondary)]">
                      <ArrowRight size={14} className="mt-1 shrink-0 text-[var(--fg-muted)]" />
                      <span>{ach}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="t-eyebrow text-[var(--fg-primary)] mb-3 flex items-center gap-1.5">
                  <Code size={14} className="text-[var(--fg-secondary)]" />
                  Technologies
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {currentExp.technologies.map((tech) => (
                    <Badge key={tech}>{tech}</Badge>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="t-eyebrow text-[var(--fg-primary)] mb-3 flex items-center gap-1.5">
                  <TrendingUp size={14} className="text-[var(--fg-secondary)]" />
                  Proven Impact
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(currentExp.impact).map(([key, val]) => (
                    <div key={key} className="p-3 rounded-md bg-[var(--bg-recessed)] text-center">
                      <p className="t-h6 text-[var(--fg-primary)] font-semibold mb-0.5">{val}</p>
                      <p className="text-[10px] text-[var(--fg-secondary)] capitalize font-mono">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          ) : (
            <Card className="p-12 text-center text-[var(--fg-secondary)]">
              <p>Select an experience to view details.</p>
            </Card>
          )}
        </div>
      </div>
    </Section>
  );
}
